/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {coerceNumberProperty} from "@angular/cdk/coercion";
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {TrecFreqType} from "@trec/api/clinical-data";

export type TrecFreqTypeExtended = TrecFreqType | "every_day";

export interface FrequencySelectorData {
  type: TrecFreqType;
  value: number | number[] | null;
}

export const FREQUENCY_SELECTOR_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => FrequencySelectorComponent),
  multi: true,
};

/**
 * Select a frequency type and its value.
 * For therapies, tasks, exams, ...
 *
 * Implements ControlValueAccessor (see: https://netbasal.com/angular-custom-form-controls-made-easy-4f963341c8e2)
 */
@Component({
  selector: "c3-frequency-selector",
  templateUrl: "./frequency-selector.component.html",
  styleUrls: ["./frequency-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FREQUENCY_SELECTOR_VALUE_ACCESSOR],
  standalone: false
})
export class FrequencySelectorComponent implements ControlValueAccessor {
  /**
   * Array with the number of the days for a month.
   */
  readonly DAYS_IN_MONTH: number[] = new Array(31).fill(0).map<number>((_, i) => i + 1);

  /**
   * Angular control's on change function.
   */
  onChange?: (data: FrequencySelectorData) => void;

  /**
   * Angular control's on touched function.
   */
  onTouched?: () => void;

  disabled = false;

  freqType: TrecFreqTypeExtended = "at_need";

  private _freqValue: number | number[] | null = null;
  public get freqValue(): number | number[] | null {
    return this._freqValue;
  }
  public set freqValue(value: number | number[] | null) {
    this._freqValue = value;
  }

  constructor(private cd: ChangeDetectorRef) { }

  // Callbacks ////////////////////////////////////////////////////////////////////////////////////

  onTypeChange(value: TrecFreqTypeExtended) {
    this.freqType = value;

    // Normalize type
    const type: TrecFreqType = this.normalizeType(this.freqType);

    // Set new value
    switch (this.freqType) {
      case "at_need":
        this.freqValue = null;
        break;
      case "every_day":
        this.freqValue = 1;
        break;
      case "every_x_days":
        this.freqValue = 2;
        break;
      case "day_of_week":
      case "day_of_month":
        this.freqValue = [];
        break;
      default:
        this.freqValue = null;
        break;
    }

    // Emit change
    this.triggerControlValueAccessorChange({type, value: this.freqValue});
  }

  onWeekDayToggle(event: CustomEvent) {
    const weekDayNumber: number = coerceNumberProperty(event.detail.value);
    const checked: boolean = event.detail.checked;

    let newValue = Array.isArray(this.freqValue) ? this.freqValue : [];

    if (checked) {
      if (!newValue.includes(weekDayNumber)) {
        newValue = [...newValue, weekDayNumber];
      }
    } else {
      newValue = newValue.filter((day) => day !== weekDayNumber);
    }

    this.onValueChange(newValue);
  }

  onMonthDayToggle(day: number) {
    let newValue = Array.isArray(this.freqValue) ? this.freqValue : [];
    const toCheck = !newValue.includes(day);

    if (toCheck) {
      newValue = [...newValue, day];
    } else {
      newValue = newValue.filter((d) => d !== day);
    }

    this.onValueChange(newValue);
  }

  onValueChange(newValue: string | number | number[] | null) {
    // Special case
    if (Array.isArray(newValue)) {
      newValue = [...newValue].sort();
    } else if (typeof newValue === "string") {
      newValue = coerceNumberProperty(newValue);
    }

    // Set new value
    this.freqValue = newValue;

    // Emit change
    this.triggerControlValueAccessorChange({
      type: this.normalizeType(this.freqType),
      value: this.freqValue,
    });
  }

  // Control value accessor methods ///////////////////////////////////////////////////////////////

  writeValue(data?: FrequencySelectorData): void {
    this.freqType = data ? data.type : "at_need";
    this.freqValue = data ? data.value : null;

    // Special cases
    if (
      this.freqType === "every_x_days" &&
      (this.freqValue === 1 || (this.freqValue && this.freqValue[0]) === 1)
    ) {
      this.freqType = "every_day";
    }

    this.cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  private triggerControlValueAccessorChange(data: FrequencySelectorData) {
    if (!this.onChange || !this.onTouched) {
      throw new Error("ControlValueAccessor still not initialized");
    }

    this.onTouched();
    this.onChange(data);
  }

  /**
   * Convert special case 'every_day' into 'every_x_days'
   *
   * @param type Frequency type
   */
  private normalizeType(type: TrecFreqTypeExtended): TrecFreqType {
    return type === "every_day" ? "every_x_days" : type;
  }
}
